import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectItem } from 'primeng';
import { Document } from '../../../jurisdiction/jurisdiction.domain';
import { CommonService } from '../../../services/common.service';
import { NetzTranslateService } from '../../../services/netz-translate.service';
import { ElectricalGenerator } from '../../entities/electricalGenerator';
import { EnergyProducer } from '../../entities/energyProducer';
import {
  EnergySource,
  EnergyType,
  UICollectionTypes,
  TechnicalSpecificationsFormSections,
} from '../../entities/enums/ui-collection-types.enum';
import { PhotovoltaicModule } from '../../entities/photovoltaicModule';
import { PowerInverter } from '../../entities/powerInverter';
import { StorageData } from '../../entities/storageData';
import { StorageUnit } from '../../entities/storageUnit';
import { ErrorService } from '../../../services/error/error.service';
import { IntegratedGridProtectionDocument } from '../../entities/integratedGridProtectionDocument';
import { TechnicalData } from '../../entities/technicalData';
import {
  ENERGY_PRODUCERS,
  ENERGY_SOURCES,
  ENERGY_TYPES,
  MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION,
  STORAGE_CONNECTIONS,
} from '../../constants/grid-application-constants';
import { detectDecimalSeparator } from '../../utils/utils';
import { LocalStorageService } from '../../../services/local-storage.service';
import { GridApplicationDocumentTypes } from '../../entities/enums/grid-application-document-types.enum';
import { DestroyableComponent } from '../../../common/destroyable.component';
import { distinctUntilChanged, first, map, switchMap } from 'rxjs/operators';
import { EnvironmentService } from '../../../services/environment.service';
import { NgForm } from '@angular/forms';
import { FileService } from '../../../services/file.service';
import { HttpResponse } from '@angular/common/http';
import { FileEvent } from '../../../+shared/models/fileEvent';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EditabilityBasedOnProcessStatus } from '../../entities/editabilityBasedOnProcessStatus';
import { GridApplicationService } from 'src/app/+grid-application-shared/services/grid-application.service';
import { ComponentCanDeactivate } from 'src/app/+shared/guards/pending-changes-guard.servie';
import { GridApplication } from '../../entities/gridApplication';
import { GridApplicationCommentsService } from '../../services/grid-application-comments.service';

@Component({
  template: '',
})
export abstract class TechnicalFormComponent<GridApp extends GridApplication>
  extends DestroyableComponent
  implements OnInit, ComponentCanDeactivate
{
  @ViewChild('technicalForm', { read: NgForm }) set technicalFormSetter(value: NgForm) {
    this.technicalForm = value;

    if (this.technicalForm) {
      this.preventLeak(this.technicalForm.valueChanges).subscribe(() => {
        this.isTechnicalFormValidSubject$.next(this.technicalForm?.valid || this.technicalForm?.form.status === 'DISABLED');
        this.isTechnicalFormDirtySubject$.next(this.technicalForm?.dirty);
      });
    }
  }

  protected applicationNotFinished: boolean;

  public ENERGY_PRODUCERS = ENERGY_PRODUCERS;
  public ENERGY_TYPES = ENERGY_TYPES;
  public ENERGY_SOURCES = ENERGY_SOURCES;

  public applicationId: number;

  public TechnicalSpecificationsFormSections = TechnicalSpecificationsFormSections;
  public documentTypes = GridApplicationDocumentTypes;
  public technicalData: TechnicalData = new TechnicalData();
  public step = 2;

  powerStorageExists: boolean;
  isIntegratedGridProtection: boolean;
  isCentralGridProtection: boolean;
  isLoading: boolean;
  isDetailsView: boolean = false;
  role: string = this.localStorageService.getItem('role');
  isBaseDataCommentVisible: boolean = false;
  isExistingEnergyProducerCommentVisible: boolean = false;
  isLockUnlockButtonClicked: boolean = false;
  sectionComment: string;
  externalApplicationId: string;
  preventCanDeactivateCheck: boolean = false;

  private isTechnicalFormValidSubject$: Subject<boolean> = new BehaviorSubject(this.isDetailsView);
  private isTechnicalFormDirtySubject$: Subject<boolean> = new BehaviorSubject(false);
  isTechnicalFormValid$: Observable<boolean> = this.isTechnicalFormValidSubject$.asObservable().pipe(distinctUntilChanged());
  isTechnicalFormDirty$: Observable<boolean> = this.isTechnicalFormDirtySubject$.asObservable().pipe(distinctUntilChanged());

  hideCommodityWater: boolean = this.environmentService.hideCommodityWater;
  showFotoProofPhotovoltaicModules: boolean = this.environmentService.showFotoProofPhotovoltaicModules;
  technicalForm: NgForm;

  typesOfEnergyProducers: SelectItem[];
  availableEnergySources: SelectItem[];
  energySources: SelectItem[];

  public editabilityBasedOnProcessStatus: EditabilityBasedOnProcessStatus = new EditabilityBasedOnProcessStatus();
  public detectDecimalSeparator = detectDecimalSeparator;
  isTechnicalFormDirty: boolean;

  constructor(
    protected fileService: FileService,
    protected translate: NetzTranslateService,
    protected commonService: CommonService,
    protected service: GridApplicationService<GridApp>,
    protected commentService: GridApplicationCommentsService,
    protected errorService: ErrorService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected localStorageService: LocalStorageService,
    protected environmentService: EnvironmentService,
    protected translateService: TranslateService
  ) {
    super();
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (this.preventCanDeactivateCheck) {
      return true;
    }

    // if wea re in details view show warning always
    if (this.isDetailsView) return false;

    return !this.technicalForm?.dirty;
  }

  ngOnInit(): void {
    this.isLoading = true;

    // force to load translations if needed
    if (!Object.keys(this.translateService.store.translations).length) {
      this.translateService.use(this.translate.getCurrentLang()).subscribe();
    }

    this.initDropdownCollections();
    this.energySources = this.availableEnergySources;
    this.registerForLanguageChange();

    this.applicationId = Number(this.route.snapshot.paramMap.get('id'));
    this.applicationNotFinished = JSON.parse(localStorage.getItem('update_application')) != null || !this.applicationId;

    if (this.applicationId) {
      this.loadApplicationData();
    } else if (this.applicationNotFinished) {
      this.applicationId = Number(JSON.parse(localStorage.getItem('application_id')));
      this.technicalData.application_id = this.applicationId;

      this.preventLeak(this.service.getApplication(this.applicationId)).subscribe(
        application => {
          if (Object.keys(application.technical_data).length > 0) {
            this.prepareDetails(application.technical_data);
            this.prepareDocumentsForDetails(application.document);
          } else {
            this.applicationNotFinished = false;
          }

          this.isLoading = false;
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.technicalData.application_id = Number(JSON.parse(localStorage.getItem('application_id')));
      this.isLoading = false;
    }

    this.isTechnicalFormDirty$.subscribe(isDirty => (this.isTechnicalFormDirty = isDirty));
  }

  public getEnergySources(energyProducer: EnergyProducer): SelectItem[] {
    const energyProducerEnergyType = energyProducer.energy_type;

    if (
      energyProducerEnergyType == null ||
      energyProducerEnergyType === ENERGY_TYPES.STORAGE ||
      energyProducerEnergyType === ENERGY_TYPES.OTHER
    ) {
      return this.availableEnergySources;
    }

    if (energyProducerEnergyType === ENERGY_TYPES.PHOTOVOLTAICS) {
      return this.availableEnergySources.filter(s => s.value === ENERGY_SOURCES.SOLAR);
    }

    if (energyProducerEnergyType === ENERGY_TYPES.WIND_POWER_PLANT) {
      return this.availableEnergySources.filter(s => s.value === ENERGY_SOURCES.WIND);
    }

    if (energyProducerEnergyType === ENERGY_TYPES.HYDRO_POWER_PLANT) {
      return this.availableEnergySources.filter(s => s.value === ENERGY_SOURCES.WATER);
    }

    return this.availableEnergySources.filter(
      s => s.value !== ENERGY_SOURCES.SOLAR && s.value !== ENERGY_SOURCES.WIND && s.value !== ENERGY_SOURCES.WATER
    );
  }

  public setEnergySource(energyProducer: EnergyProducer): void {
    if (energyProducer.is_main) {
      this.clearOptionalField(energyProducer);
      this.clearEnergySourceRelatedData(energyProducer);
      this.setupGridProtectionDocuments();
    }

    switch (energyProducer.energy_type) {
      case ENERGY_TYPES.PHOTOVOLTAICS:
        energyProducer.energy_source = ENERGY_SOURCES.SOLAR;

        if (energyProducer.is_main) {
          this.technicalData.photovoltaic_modules.push(new PhotovoltaicModule());
          this.technicalData.power_inverters.push(new PowerInverter());
          if (this.isIntegratedGridProtection) {
            this.addGridProtectionDoc(UICollectionTypes.PowerInverter);

            if (this.powerStorageExists && this.technicalData.storage_data.storage_connection === STORAGE_CONNECTIONS.AC) {
              this.addGridProtectionDoc(UICollectionTypes.PowerConverter);
            }
          }
        }
        break;
      case ENERGY_TYPES.STORAGE:
        energyProducer.energy_source = null;

        if (energyProducer.is_main) {
          this.technicalData.has_power_storage = null;
          this.technicalData.storage_data = new StorageData();
          this.technicalData.storages.push(new StorageUnit());
        }
        break;
      case ENERGY_TYPES.WIND_POWER_PLANT:
        energyProducer.energy_source = ENERGY_SOURCES.WIND;

        if (energyProducer.is_main) {
          this.technicalData.generators.push(new ElectricalGenerator());
          if (this.isIntegratedGridProtection) {
            this.addGridProtectionDoc(UICollectionTypes.Generator);

            if (this.powerStorageExists && this.technicalData.storage_data.storage_connection === STORAGE_CONNECTIONS.AC) {
              this.addGridProtectionDoc(UICollectionTypes.PowerConverter);
            }
          }
        }
        break;
      case ENERGY_TYPES.HYDRO_POWER_PLANT:
        energyProducer.energy_source = ENERGY_SOURCES.WATER;

        if (energyProducer.is_main) {
          this.technicalData.generators.push(new ElectricalGenerator());
          if (this.isIntegratedGridProtection) {
            this.addGridProtectionDoc(UICollectionTypes.Generator);

            if (this.powerStorageExists && this.technicalData.storage_data.storage_connection === STORAGE_CONNECTIONS.AC) {
              this.addGridProtectionDoc(UICollectionTypes.PowerConverter);
            }
          }
        }
        break;
      default:
        energyProducer.energy_source = null;

        if (energyProducer.is_main) {
          this.technicalData.generators.push(new ElectricalGenerator());
          if (this.isIntegratedGridProtection) {
            this.addGridProtectionDoc(UICollectionTypes.Generator);

            if (this.powerStorageExists && this.technicalData.storage_data.storage_connection === STORAGE_CONNECTIONS.AC) {
              this.addGridProtectionDoc(UICollectionTypes.PowerConverter);
            }
          }
        }
        break;
    }

    this.hasPowerStorageChange(null);
  }

  public addElement(collection: any[], type: string): void {
    switch (type) {
      case UICollectionTypes.ExistingProducer:
        collection.push(new EnergyProducer(null, null, false));
        break;
    }
  }

  public removeElement(element: any, type: string): void {
    switch (type) {
      case UICollectionTypes.ExistingProducer:
        this.technicalData.existing_energy_producers = this.technicalData.existing_energy_producers.filter(x => x !== element);
        break;
    }
  }

  public saveFormData(shouldProceed: boolean) {
    if (this.isDetailsView || this.applicationNotFinished) {
      return this.updateForm(shouldProceed);
    }

    this.createForm();
  }

  public hasPowerStorageChange(hasPowerStorage?: boolean): void {
    if (hasPowerStorage && !this.technicalData.storages.length) {
      this.powerStorageExists = true;
      this.technicalData.storage_data = new StorageData();
      this.technicalData.storages.push(new StorageUnit());
      return;
    }

    if (hasPowerStorage === false) {
      this.powerStorageExists = false;
      this.technicalData.storage_data = null;
      this.technicalData.storages = [];
      this.technicalData.power_converters = [];
      this.removeGridProtectionDocsByType(UICollectionTypes.PowerConverter);
      return;
    }

    this.powerStorageExists =
      this.technicalData.existing_energy_producers[0].energy_type === ENERGY_TYPES.STORAGE ||
      this.technicalData.has_power_storage ||
      !!hasPowerStorage;
  }

  public hasEnergyProducerChange(hasEnergyProducer: boolean): void {
    if (!hasEnergyProducer) {
      this.technicalData.existing_energy_producers = this.technicalData.existing_energy_producers.slice(0, 1);
      return;
    }

    if (this.technicalData.existing_energy_producers.length === 1) {
      this.technicalData.existing_energy_producers.push(new EnergyProducer(null, null, false));
    }
  }

  public setupGridProtectionDocuments(): void {
    const totalFeedInPower = this.technicalData.existing_energy_producers[0].total_feed_in_power;
    if (!totalFeedInPower) {
      return;
    }

    if (totalFeedInPower > MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION) {
      return this.setupCentralGridProtectionDocuments();
    }

    this.setupIntegratedGridProtectionDocuments();
  }

  public lockSection(section: TechnicalSpecificationsFormSections): void {
    if (this.isModeratorRole()) {
      switch (section) {
        case TechnicalSpecificationsFormSections.BaseData:
          this.technicalData.technical_specifications_sections_state.is_base_data_locked = this.technicalData
            .technical_specifications_sections_state.is_base_data_locked
            ? null
            : true;
          break;
        case TechnicalSpecificationsFormSections.ExistingEnergyProducer:
          this.technicalData.technical_specifications_sections_state.is_existing_energy_producer_locked = this.technicalData
            .technical_specifications_sections_state.is_existing_energy_producer_locked
            ? null
            : true;
          break;
      }

      this.isLockUnlockButtonClicked = true;
    }
  }

  public unlockSection(section: TechnicalSpecificationsFormSections): void {
    if (this.isModeratorRole()) {
      switch (section) {
        case TechnicalSpecificationsFormSections.BaseData:
          if (this.technicalData.technical_specifications_sections_state.is_base_data_locked === false) {
            this.technicalData.technical_specifications_sections_state.is_base_data_locked = null;
          } else {
            this.technicalData.technical_specifications_sections_state.is_base_data_locked = false;
            if (!this.isBaseDataCommentVisible) {
              this.showComment(section);
            }
          }
          break;
        case TechnicalSpecificationsFormSections.ExistingEnergyProducer:
          if (this.technicalData.technical_specifications_sections_state.is_existing_energy_producer_locked === false) {
            this.technicalData.technical_specifications_sections_state.is_existing_energy_producer_locked = null;
          } else {
            this.technicalData.technical_specifications_sections_state.is_existing_energy_producer_locked = false;
            if (!this.isExistingEnergyProducerCommentVisible) {
              this.showComment(section);
            }
          }
          break;
      }
      this.isLockUnlockButtonClicked = true;
    }
  }

  public showComment(commentSection: TechnicalSpecificationsFormSections): void {
    if (this.isModeratorRole() && this.editabilityBasedOnProcessStatus.internalRoles) {
      switch (commentSection) {
        case TechnicalSpecificationsFormSections.BaseData:
          this.isBaseDataCommentVisible = !(
            this.isBaseDataCommentVisible && !this.technicalData.technical_specifications_sections_state.base_data_comment
          );
          break;
        case TechnicalSpecificationsFormSections.ExistingEnergyProducer:
          this.isExistingEnergyProducerCommentVisible = !(
            this.isExistingEnergyProducerCommentVisible &&
            !this.technicalData.technical_specifications_sections_state.existing_energy_producer_comment
          );
          break;
      }
    }
  }

  public async updateComment(data: any): Promise<void> {
    if (this.isModeratorRole() && this.editabilityBasedOnProcessStatus.internalRoles) {
      switch (data.section) {
        case TechnicalSpecificationsFormSections.BaseData:
          this.technicalData.technical_specifications_sections_state.base_data_comment = data.value;
          break;
        case TechnicalSpecificationsFormSections.ExistingEnergyProducer:
          this.technicalData.technical_specifications_sections_state.existing_energy_producer_comment = data.value;
          break;
      }

      await this.saveComment();
    }
  }

  public async deleteComment(section: TechnicalSpecificationsFormSections): Promise<void> {
    if (this.isModeratorRole() && this.editabilityBasedOnProcessStatus.internalRoles) {
      switch (section) {
        case TechnicalSpecificationsFormSections.BaseData:
          this.technicalData.technical_specifications_sections_state.base_data_comment = null;
          break;
        case TechnicalSpecificationsFormSections.ExistingEnergyProducer:
          this.technicalData.technical_specifications_sections_state.existing_energy_producer_comment = null;
          break;
      }

      await this.saveComment();
    }
  }

  public isEditableByExternalRoles(): boolean {
    return (
      (this.isTechnicianRole() && this.editabilityBasedOnProcessStatus.technicianRole) ||
      (this.isCustomerRole() && this.editabilityBasedOnProcessStatus.customerRole)
    );
  }

  public isModeratorRole(): boolean {
    return this.role === 'employee' || this.role === 'administrator';
  }

  protected isTechnicianRole(): boolean {
    return this.role === 'standard_technician' || this.role === 'guest_technician';
  }

  protected isCustomerRole(): boolean {
    return this.role === 'customer';
  }

  public clearFile(fileEvent: FileEvent): void {
    if (!fileEvent.documentId) return;
    if (!this.technicalData.document_ids_for_deletion) this.technicalData.document_ids_for_deletion = [];
    this.technicalData.document_ids_for_deletion.push(fileEvent.documentId);
    this.technicalForm.form.markAsDirty();
  }

  protected async saveComment(): Promise<void> {
    if (this.technicalData.technical_specifications_sections_state.application_id) {
      const techDataResponse = await this.commentService
        .updateTechnicalSpecificationsSectionComment(this.technicalData.technical_specifications_sections_state)
        .pipe(first())
        .toPromise();
    } else {
      this.technicalData.technical_specifications_sections_state.application_id = this.applicationId;
      const techDataResponse = await this.commentService
        .createTechnicalSpecificationsSectionComment(this.technicalData.technical_specifications_sections_state)
        .pipe(first())
        .toPromise();
    }
  }

  public isBaseDataDisabled(): boolean {
    return (
      this.technicalData.technical_specifications_sections_state?.is_base_data_locked !== true &&
      this.isDisabledRespectToRole() &&
      this.isDetailsView
    );
  }

  public isExistingEnergyProducerDisabled(): boolean {
    return (
      this.technicalData.technical_specifications_sections_state?.is_existing_energy_producer_locked !== true &&
      this.isDisabledRespectToRole() &&
      this.isDetailsView
    );
  }

  public downloadAllDocuments(): void {
    this.fileService.getAllDocumentsByApplicationId(this.applicationId.toString()).subscribe(
      (response: HttpResponse<Blob>) => {
        this.errorService.throwErrorIfFailure(response, false);
        this.fileService.downloadZipDocument(response);
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  protected abstract loadOrderProcess(): void;

  protected abstract proceed(): void;

  protected loadApplicationData() {
    this.loadOrderProcess();

    this.preventLeak(this.service.getApplication(this.applicationId)).subscribe(
      application => {
        this.externalApplicationId = application.application_ext_id;
        this.prepareDetails(application.technical_data);
        this.technicalData.technical_specifications_sections_state = application.technical_specifications_sections_state;

        this.prepareDocumentsForDetails(application.document);
        application.process_status = this.editabilityBasedOnProcessStatus;

        this.isDetailsView = true;
        this.isLoading = false;
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  protected createForm() {
    this.isLoading = true;

    this.preventLeak(this.service.createTechnicalData(this.technicalData))
      .pipe(
        switchMap(response => {
          this.applicationNotFinished = true;
          const preparedDocuments = this.prepareDocumentsForUpload(response);
          return this.service.uploadDocuments(preparedDocuments, this.applicationId.toString());
        })
      )
      .subscribe(
        () => {
          this.isLoading = false;
          this.proceed();
        },
        error => {
          this.isLoading = false;
          return this.errorService.throwErrorIfFailure(error, true);
        }
      );
  }

  protected updateForm(shouldProceed: boolean) {
    if (this.role === 'observer' && shouldProceed) {
      return this.proceed();
    }

    this.isLoading = true;

    this.preventLeak(this.service.updateTechnicalData(this.technicalData))
      .pipe(
        switchMap(response => {
          const preparedDocuments = this.prepareDocumentsForUpload(response);
          return this.service.updateDocuments(preparedDocuments, this.applicationId.toString());
        })
      )
      .subscribe(
        () => {
          this.isLoading = false;
          this.isLockUnlockButtonClicked = false;
          this.updateLocalStorageForSectionStateAndProcessStatus();

          if (this.applicationId && !shouldProceed) {
            return this.loadApplicationData();
          }

          this.proceed();
        },
        error => {
          this.isLoading = false;
          return this.errorService.throwErrorIfFailure(error, true);
        }
      );
  }

  protected addGridProtectionDoc(documentType: string): void {
    if (this.isIntegratedGridProtection) {
      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.push(
        new IntegratedGridProtectionDocument(documentType)
      );
    }
  }

  protected removeGridProtectionDoc(index: number, unitType: string): void {
    if (this.isIntegratedGridProtection) {
      const documentsToFilter = this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(
        d => d.unit_type === unitType
      );

      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents =
        this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(d => d.unit_type !== unitType);

      documentsToFilter.splice(index, 1);

      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.push(...documentsToFilter);
    }
  }

  protected removeGridProtectionDocsByType(documentType: string): void {
    if (this.isIntegratedGridProtection) {
      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents =
        this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(x => x.unit_type !== documentType);
    }
  }

  protected initDropdownCollections(): void {
    this.typesOfEnergyProducers = [
      { label: this.translate.translateString('grid-comp.energyTypes.photovoltaics'), value: 0 },
      { label: this.translate.translateString('grid-comp.energyTypes.storage'), value: 1 },
      { label: this.translate.translateString('grid-comp.energyTypes.bhkw'), value: 2 },
      { label: this.translate.translateString('grid-comp.energyTypes.wind'), value: 3 },
      ...((!this.hideCommodityWater && [{ label: this.translate.translateString('grid-comp.energyTypes.water'), value: 4 }]) || []),
      { label: this.translate.translateString('grid-comp.energyTypes.fuel'), value: 5 },
      { label: this.translate.translateString('grid-comp.energyTypes.other'), value: 6 },
    ];

    this.availableEnergySources = [
      { label: this.translate.translateString('grid-comp.energySources.solar'), value: ENERGY_SOURCES.SOLAR },
      { label: this.translate.translateString('grid-comp.energySources.wind'), value: ENERGY_SOURCES.WIND },
      ...((!this.hideCommodityWater && [
        { label: this.translate.translateString('grid-comp.energySources.water'), value: ENERGY_SOURCES.WATER },
      ]) ||
        []),
      { label: this.translate.translateString('grid-comp.energySources.biomass'), value: ENERGY_SOURCES.BIOMASS },
      { label: this.translate.translateString('grid-comp.energySources.naturalGas'), value: ENERGY_SOURCES.NATURAL_GAS },
      { label: this.translate.translateString('grid-comp.energySources.other'), value: ENERGY_SOURCES.OTHER },
    ];
  }

  protected registerForLanguageChange(): void {
    this.preventLeak(this.translate.registerForLanguageChange()).subscribe(() => {
      this.initDropdownCollections();
    });
  }

  protected clearOptionalField(energyProducer: EnergyProducer): void {
    energyProducer.other_energy_type = null;
    energyProducer.other_energy_source = null;
    energyProducer.total_feed_in_power = null;
  }

  protected clearEnergySourceRelatedData(energyProducer: EnergyProducer): void {
    this.technicalData.photovoltaic_modules = [];
    this.technicalData.power_inverters = [];
    this.technicalData.generators = [];
    this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents = [];

    if (!this.powerStorageExists || energyProducer.energy_type === ENERGY_TYPES.STORAGE) {
      this.technicalData.storages = [];
      this.technicalData.power_converters = [];
      this.technicalData.storage_data = null;
    }
  }

  protected prepareDetails(data: TechnicalData): void {
    const hasPowerStorage = JSON.parse(data.has_power_storage + '');
    const hasEnergyProducer = JSON.parse(data.has_energy_producer + '');

    this.technicalData = data;

    this.convertEnergyProducersEnumValues(data);
    this.technicalData.has_power_storage = hasPowerStorage;
    this.powerStorageExists = hasPowerStorage || data.existing_energy_producers[0]?.energy_type === ENERGY_TYPES.STORAGE;
    this.technicalData.has_energy_producer = hasEnergyProducer;
    this.hasEnergyProducerChange(hasEnergyProducer);
    this.isIntegratedGridProtection =
      this.technicalData.existing_energy_producers[0]?.total_feed_in_power <= MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION;
    this.isCentralGridProtection =
      this.technicalData.existing_energy_producers[0]?.total_feed_in_power > MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION;
  }

  protected convertEnergyProducersEnumValues(data: TechnicalData): void {
    this.technicalData.existing_energy_producers.forEach((ep, index) => {
      ep.energy_type = Number(EnergyType[data.existing_energy_producers[index].energy_type]);
      ep.energy_source = Number(EnergySource[data.existing_energy_producers[index].energy_source]);
    });
  }

  protected prepareDocumentsForUpload(outputTechData: TechnicalData): Document[] {
    let documentsToUpload: Document[] = [];

    if (this.technicalData.photovoltaic_modules?.length > 0) {
      const photovoltaicModules = outputTechData.photovoltaic_modules;

      this.technicalData.photovoltaic_modules.forEach((pm, index) => {
        if (pm.data_sheet?.file) {
          pm.data_sheet.photovoltaic_id = photovoltaicModules[index].id;
          documentsToUpload.push(pm.data_sheet);
        }
      });

      this.technicalData.photovoltaic_modules.forEach((pm, pmIndex) => {
        pm.foto_proof_photovoltaic_modules?.forEach(foto => {
          if (foto.file) {
            foto.photovoltaic_id = photovoltaicModules[pmIndex].id;
            documentsToUpload.push(foto);
          }
        });
      });

      const inverters = outputTechData.power_inverters;
      this.technicalData.power_inverters.forEach((pi, index) => {
        pi.documents.forEach(d => {
          if (d.file) {
            d.inverter_id = inverters[index].id;
            documentsToUpload.push(d);
          }
        });
      });
    } else if (this.technicalData.generators?.length > 0) {
      const generators = outputTechData.generators;

      this.technicalData.generators.forEach((g, index) => {
        g.documents.forEach(d => {
          if (d.file) {
            d.generator_id = generators[index].id;
            documentsToUpload.push(d);
          }
        });
      });
    }

    if (this.technicalData.power_converters?.length > 0) {
      const converters = outputTechData.power_converters;

      this.technicalData.power_converters.forEach((pc, index) => {
        pc.documents.forEach(d => {
          if (d.file) {
            d.converter_id = converters[index].id;
            documentsToUpload.push(d);
          }
        });
      });
    }

    if (this.technicalData.grid_power_plant_protection_data?.central_grid_protection_documents?.length > 0) {
      const gridProtection = outputTechData.grid_power_plant_protection_data;

      this.technicalData.grid_power_plant_protection_data.central_grid_protection_documents.forEach(document => {
        if (document.file) {
          document.power_plant_protection_id = gridProtection.id;
          documentsToUpload.push(document);
        }
      });
    }

    if (this.technicalData.grid_power_plant_protection_data?.integrated_grid_protection_documents?.length > 0) {
      const integratedGridDocs = outputTechData.grid_power_plant_protection_data.integrated_grid_protection_documents;

      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.forEach((integratedGridDocument, index) => {
        integratedGridDocument.documents.forEach(document => {
          if (document.file) {
            document.integrated_protection_id = integratedGridDocs[index].id;
            documentsToUpload.push(document);
          }
        });
      });
    }

    if (this.technicalData.circuit_diagram.file) {
      documentsToUpload.push(this.technicalData.circuit_diagram);
    }

    if (this.technicalData.list_of_data_points?.file) {
      documentsToUpload.push(this.technicalData.list_of_data_points);
    }

    return documentsToUpload;
  }

  protected prepareDocumentsForDetails(documents: Document[]): void {
    const findMissingDocuments = (documents: Document[], types: GridApplicationDocumentTypes[]) => {
      const missingDocuments = [];

      for (const type of types) {
        if (!documents?.find(d => d.sk_document_type === type)) {
          missingDocuments.push(new Document(type));
        }
      }

      return missingDocuments;
    };

    documents = documents?.sort((a, b) => parseInt(b.document_id) - parseInt(a.document_id));

    const photovoltaicDocs = this.groupByPhotovoltaicId(documents);
    const integratedGridDocs = documents.filter(d => d.integrated_protection_id > 0);

    this.technicalData.photovoltaic_modules.forEach(x => {
      x.data_sheet =
        photovoltaicDocs[x.id]?.find(d => d.sk_document_type === GridApplicationDocumentTypes.PhotovoltaicDataSheet) ||
        new Document(GridApplicationDocumentTypes.PhotovoltaicDataSheet);
      x.foto_proof_photovoltaic_modules = photovoltaicDocs[x.id]?.filter(
        d => d.sk_document_type === GridApplicationDocumentTypes.FotoProofPhotovoltaicModules
      ) || [new Document(GridApplicationDocumentTypes.FotoProofPhotovoltaicModules)];
    });

    this.technicalData.power_inverters.forEach(x => {
      const docs = documents.filter(d => d.inverter_id == x.id);
      const requiredDocTypes = [GridApplicationDocumentTypes.EnergyUnitDataSheet, GridApplicationDocumentTypes.UnitCertificate];
      x.documents = [...docs, ...findMissingDocuments(docs, requiredDocTypes)];
    });

    this.technicalData.generators.forEach(x => {
      const docs = documents.filter(d => d.generator_id == x.id);
      const requiredDocTypes = [GridApplicationDocumentTypes.EnergyUnitDataSheet, GridApplicationDocumentTypes.UnitCertificate];
      x.documents = [...docs, ...findMissingDocuments(docs, requiredDocTypes)];
    });

    this.technicalData.power_converters.forEach(x => {
      const docs = documents.filter(d => d.converter_id == x.id);
      const requiredDocTypes = [GridApplicationDocumentTypes.EnergyUnitDataSheet, GridApplicationDocumentTypes.UnitCertificate];
      x.documents = [...docs, ...findMissingDocuments(docs, requiredDocTypes)];
    });

    this.technicalData.grid_power_plant_protection_data.central_grid_protection_documents = [
      documents.find(
        d =>
          d.power_plant_protection_id == this.technicalData.grid_power_plant_protection_data.id &&
          d.sk_document_type == GridApplicationDocumentTypes.CentralCertificate
      ) || new Document(GridApplicationDocumentTypes.CentralCertificate),
      documents.find(
        d =>
          d.power_plant_protection_id == this.technicalData.grid_power_plant_protection_data.id &&
          d.sk_document_type == GridApplicationDocumentTypes.CentralAudit
      ) || new Document(GridApplicationDocumentTypes.CentralAudit),
    ];

    this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.forEach(integratedDocument => {
      const docs = integratedGridDocs.filter(d => d.integrated_protection_id == integratedDocument.id);
      const requiredDocTypes = [GridApplicationDocumentTypes.IntegratedCertificate, GridApplicationDocumentTypes.IntegratedAudit];
      integratedDocument.documents = [...docs, ...findMissingDocuments(docs, requiredDocTypes)];
    });

    this.fillInntegratedGridProtectionEmptyDocs();

    this.technicalData.circuit_diagram =
      documents.find(d => d.sk_document_type === GridApplicationDocumentTypes.CircuitDiagram) ||
      new Document(GridApplicationDocumentTypes.CircuitDiagram);
  }

  protected fillInntegratedGridProtectionEmptyDocs(): void {
    const generatorDocs = this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(
      x => x.unit_type === UICollectionTypes.Generator
    );

    if (this.technicalData.generators.length) {
      this.fillInEmptyDocs(generatorDocs, UICollectionTypes.Generator);
    }

    const powerInverterDocs = this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(
      x => x.unit_type === UICollectionTypes.PowerInverter
    );

    if (this.technicalData.power_inverters.length) {
      this.fillInEmptyDocs(powerInverterDocs, UICollectionTypes.PowerInverter);
    }

    const powerConverterDocs = this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(
      x => x.unit_type === UICollectionTypes.PowerConverter
    );

    if (this.technicalData.power_converters.length) {
      this.fillInEmptyDocs(powerConverterDocs, UICollectionTypes.PowerConverter);
    }
  }

  protected fillInEmptyDocs(integratedGridProtectionDocument: IntegratedGridProtectionDocument[], type: UICollectionTypes): void {
    integratedGridProtectionDocument.forEach(items => {
      if (!items.documents.length) {
        items.documents.push(
          new Document(GridApplicationDocumentTypes.IntegratedCertificate),
          new Document(GridApplicationDocumentTypes.IntegratedAudit)
        );
      } else if (items.documents.length === 1) {
        const newDocType =
          items.documents[0].sk_document_type === GridApplicationDocumentTypes.IntegratedCertificate
            ? GridApplicationDocumentTypes.IntegratedAudit
            : GridApplicationDocumentTypes.IntegratedCertificate;
        items.documents.push(new Document(newDocType));
      }
    });
  }

  protected isDisabledRespectToRole(): boolean {
    if (this.isCustomerRole()) {
      return !this.editabilityBasedOnProcessStatus.customerRole;
    }
    if (this.isTechnicianRole()) {
      return !this.editabilityBasedOnProcessStatus.technicianRole;
    }

    return !this.environmentService.shouldInternalRolesBeAbleToEditApplication && (this.isModeratorRole() || this.role === 'observer');
  }

  protected updateLocalStorageForSectionStateAndProcessStatus(): void {
    this.service.application.technical_specifications_sections_state = this.technicalData.technical_specifications_sections_state;
    this.service.application.process_status = this.editabilityBasedOnProcessStatus;
  }

  protected setupCentralGridProtectionDocuments() {
    if (this.isCentralGridProtection) {
      return;
    }

    this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents = [];

    this.technicalData.grid_power_plant_protection_data.central_grid_protection_documents = [
      new Document(GridApplicationDocumentTypes.CentralCertificate),
      new Document(GridApplicationDocumentTypes.CentralAudit),
    ];

    this.isCentralGridProtection = true;
    this.isIntegratedGridProtection = false;
  }

  protected setupIntegratedGridProtectionDocuments() {
    if (this.isIntegratedGridProtection) {
      return;
    }

    this.technicalData.grid_power_plant_protection_data.central_grid_protection_documents = [];

    this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents = [
      ...this.technicalData.power_inverters.map(() => new IntegratedGridProtectionDocument(UICollectionTypes.PowerInverter)),
      ...this.technicalData.generators.map(() => new IntegratedGridProtectionDocument(UICollectionTypes.Generator)),
      ...this.technicalData.power_converters.map(() => new IntegratedGridProtectionDocument(UICollectionTypes.PowerConverter)),
    ];

    this.isCentralGridProtection = false;
    this.isIntegratedGridProtection = true;
  }

  private groupByPhotovoltaicId(documents: Document[]): { [key: number]: Document[] } {
    return documents
      .filter(d => d.photovoltaic_id > 0)
      .reduce((acc: { [key: number]: Document[] }, doc: Document) => {
        const photovoltaic_id = doc.photovoltaic_id || 0;
        if (!acc[photovoltaic_id]) {
          acc[photovoltaic_id] = [];
        }
        acc[photovoltaic_id].push(doc);
        return acc;
      }, {});
  }
}
